import React from "react";
import { Link } from "react-router-dom";

/**
 * Cluster article — "Me da vergüenza ir al gimnasio".
 * Category: shame. Deeply emotional territory.
 */

function Section({ title, children, id }) {
  return (
    <section className="space-y-4" id={id}>
      <h2 className="font-sans-display text-2xl sm:text-3xl text-[#0B2545] tracking-tight leading-snug mt-12 mb-2">
        {title}
      </h2>
      {children}
    </section>
  );
}

function Pullquote({ children }) {
  return (
    <blockquote className="border-l-2 border-[#B4C5A4] pl-5 py-1 my-6 font-sans-display text-lg sm:text-xl text-[#0B2545] tracking-tight leading-snug italic">
      {children}
    </blockquote>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//                                CATALÀ
// ════════════════════════════════════════════════════════════════════════════
export function VergonyaGimnasCA() {
  return (
    <>
      <p>
        {"T'has apuntat al gimnàs. Has pagat. Fins i tot has anat a mirar-ho un dia — has vist les màquines, els cossos, els miralls. Has agafat les claus i has sortit. No has tornat. Han passat setmanes i el rebut segueix venint al compte."}
      </p>
      <p>
        {"O potser encara no has arribat a apuntar-te. Ho penses cada gener, cada aniversari, cada estiu. Però una veu et diu: 'no és per a mi'. I aquesta veu porta anys. Aquest article és per a aquesta veu. Per entendre-la, per no lluitar-hi, i per començar a moure't d'una altra manera."}
      </p>

      <Section title="La vergonya no és feblesa. És memòria." id="memoria">
        <p>
          {"El que sents no és 'em fa mandra'. És una cosa més antiga. La vergonya del cos no apareix del no-res: és el resultat d'anys de mirades, comentaris, professionals que t'han jutjat sense saber-ho, familiars 'preocupats', roba que no et queda, fotos que evites. Cada un d'aquests moments ha deixat un dipòsit al teu sistema. Ara, quan penses en un gimnàs, aquest dipòsit s'activa tot d'una."}
        </p>
        <p>
          {"Això no és psicologia barata. És com funciona el cervell. La vergonya es guarda al mateix lloc que el perill físic — l'amígdala. El teu cos reacciona a un gimnàs com reaccionaria a una amenaça real, perquè per a ell ha estat una amenaça real durant anys."}
        </p>
        <Pullquote>
          {"No tens un problema de motivació. Tens un cos amb memòria."}
        </Pullquote>
      </Section>

      <Section title="Per què el gimnàs no és el primer pas per a tu" id="gimnas">
        <p>
          {"Tota la indústria del fitness et vendrà que el gimnàs és el lloc on comença tot. No és veritat per a tothom. Un gimnàs és un espai dissenyat per a gent que ja ha superat certes barreres: sap moure's, coneix el vocabulari, coneix el ritual. Si tu no has passat mai per aquestes fases, entrar és com anar a un país nou sense parlar l'idioma."}
        </p>
        <p>
          {"Podríem discutir molt sobre si el problema és el gimnàs o la mirada dels altres. La resposta és que no importa. Per al teu cervell, el problema és real. I la solució no és 'obligar-te'. La solució és no anar al gimnàs — encara. Comença en un lloc que no active la vergonya, i quan el cos t'ho digui, decidiràs si hi vols entrar."}
        </p>
      </Section>

      <Section title="Els espais que sí funcionen quan comences" id="espais">
        <p>
          {"Hi ha alternatives al gimnàs tradicional que no activen la vergonya, i que sovint funcionen millor perquè estan pensades per persones com tu:"}
        </p>
        <ul className="list-disc pl-6 space-y-2 marker:text-[#B4C5A4]">
          <li>{"A casa. El terra del menjador. Cinc minuts. Ningú et mira. Cap mirall. Cap comparació."}</li>
          <li>{"Passejos curts al matí primer o al vespre tard, quan hi ha menys gent al carrer."}</li>
          <li>{"Piscina municipal a hores no punta (11h del matí un dimarts, per exemple). L'aigua tapa i suporta el cos."}</li>
          <li>{"Classes específiques per a cossos com el teu — cada cop n'hi ha més: iogues per a persones amb obesitat, aiguagimnàstica adaptada, grups de caminadors."}</li>
          <li>{"Fisioterapeuta o entrenador personal en una sala privada. Sí, costa més diners que un gimnàs. Però l'única mirada és la d'un professional que està allà per ajudar-te, no per jutjar-te."}</li>
        </ul>
        <p>
          {"L'objectiu del primer any no és 'trobar el meu gimnàs'. És trobar l'espai on el teu cos pot començar a moure's sense por. Un cop hi hagi confiança, ja veurem què fer."}
        </p>
      </Section>

      <Section title="Què fer quan la vergonya apareix de sobte" id="apareix">
        <p>
          {"Aniràs començant, i un dia una veu et dirà: 'quina ridiculesa'. O 'no ho estàs fent bé'. O 'la gent et deu estar mirant'. És normal. Aquesta veu no marxa d'un dia per l'altre. Però pots aprendre a no obeir-la."}
        </p>
        <p>
          {"Tres coses que ajuden en el moment:"}
        </p>
        <ul className="list-disc pl-6 space-y-2 marker:text-[#B4C5A4]">
          <li>{"Reconèixer-la sense discutir. 'Ah, tornes a ser aquí.' No li dediquis més segons dels necessaris."}</li>
          <li>{"Preguntar-te què faria una persona que no sent el que sents tu. I fer una versió petita d'això mateix. No la versió completa — el 20% que tu pots fer avui."}</li>
          <li>{"Anotar el moment al final del dia. No pels sentiments, sinó pel patró. Amb quatre setmanes veuràs que apareix menys quan menys li fas cas."}</li>
        </ul>
        <Pullquote>
          {"La vergonya no cal vencer-la. Cal deixar-la parlar i seguir caminant."}
        </Pullquote>
      </Section>

      <Section title="La trampa de 'quan hagi perdut una mica'" id="trampa">
        <p>
          {"Molta gent es diu: 'primer perdré 5 quilos i llavors m'apunto al gimnàs'. Aquesta lògica sembla raonable. No ho és. Sense moure't, aquests 5 quilos no arribaran. I si arriben (per una dieta agressiva), tornaran. La vergonya no depèn del pes — depèn de la mirada, i la mirada la portes tu."}
        </p>
        <p>
          {"El que sí que passa quan comences a moure't (encara que no perdis pes de seguida) és que la teva relació amb el cos canvia. Comences a viure'l com a alguna cosa que respon, no com a alguna cosa que t'atrapa. Aquest canvi de percepció és el que fa que un dia el gimnàs deixi de fer por — no els 5 quilos."}
        </p>
      </Section>

      <Section title="La cosa que ningú et diu sobre la vergonya" id="ningu-diu">
        <p>
          {"A mesura que comencis a moure't, la vergonya no desapareix. Es transforma. El primer mes potser sents vergonya de sortir a caminar amb malles. El tercer mes ja no. Però potser en sents pel gest que fas quan t'aixeques d'una cadira, o pel so que fas quan respires fort. La vergonya és plàstica — es mou."}
        </p>
        <p>
          {"El que canvia no és que desaparegui, és que la teva relació amb ella canvia. La reconeixes abans, la deixes de negar, i comences a preguntar-te què t'està dient enlloc de com fer-la callar. Alguns dies encara guanyarà. Altres dies no. I això ja és molt millor que ara."}
        </p>
      </Section>

      <Section title="Un pas concret que pots fer avui" id="pas-avui">
        <p>
          {"Si has arribat fins aquí, la vergonya no t'ha guanyat encara. Aprofita això. Fes una sola cosa avui: escriu en un paper (o al mòbil) el moment concret on va aparèixer la primera mirada que et va fer vergonya. Pot ser als 12 anys, als 20, o l'any passat. No importa quan — importa recordar que la vergonya té origen. No és una veritat sobre tu."}
        </p>
        <p>
          {"Aquest gest sembla petit, però és el primer que separa la vergonya del teu present. Un cop la mous fora, dins ja hi ha una mica més d'espai per moure't sense ella."}
        </p>
        <p>
          {"Si un cop la vergonya s'ha rebaixat el que et queda són intents començats i abandonats, val la pena que llegeixis "}
          <Link to="/blog/per-que-abandono-sempre-el-canvi" className="text-[#0B2545] underline decoration-[#B4C5A4] decoration-2 underline-offset-2 hover:decoration-[#0B2545]">
            per què sempre abandones — i com trencar el patró aquesta vegada
          </Link>
          {". La vergonya és una barrera, però l'abandonament repetit té un altre origen: un mètode que et demana voluntat cada dia."}
        </p>
        <p>
          {"Si el que necessites és un mapa pràctic per començar sense passar pel gimnàs, "}
          <Link to="/blog/com-comencar-a-moure-amb-sobrepes" className="text-[#0B2545] underline decoration-[#B4C5A4] decoration-2 underline-offset-2 hover:decoration-[#0B2545]">
            {"aquesta guia t'explica com començar a moure't si tens sobrepès"}
          </Link>
          {" — cinc minuts al carrer, sense mirador i sense pressa."}
        </p>
        <p>
          {"Si vols entendre com aquesta memòria emocional del cos afecta a totes les fases del canvi (no només la vergonya al gimnàs), pots llegir la nostra guia central sobre "}
          <Link to="/blog/com-activar-te-quan-fa-anys-que-no-et-mous" className="text-[#0B2545] underline decoration-[#B4C5A4] decoration-2 underline-offset-2 hover:decoration-[#0B2545]">
            com activar-te quan fa anys que no et mous
          </Link>
          {". Allà expliquem per què el cos té memòria i com treballar-la sense forçar-te."}
        </p>
        <p>
          {"I si vols que t'ajudem a entendre com afecta la vergonya al teu punt de partida, deixa'ns el teu correu i et farem arribar un test inicial amb preguntes que et poden ajudar a veure-ho més clar."}
        </p>
      </Section>
    </>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//                                CASTELLANO
// ════════════════════════════════════════════════════════════════════════════
export function VerguenzaGimnasioES() {
  return (
    <>
      <p>
        {"Te has apuntado al gimnasio. Has pagado. Incluso has ido a verlo un día — has visto las máquinas, los cuerpos, los espejos. Has cogido las llaves y has salido. No has vuelto. Han pasado semanas y el recibo sigue viniendo a la cuenta."}
      </p>
      <p>
        {"O quizás aún no has llegado a apuntarte. Lo piensas cada enero, cada cumpleaños, cada verano. Pero una voz te dice: 'no es para mí'. Y esa voz lleva años. Este artículo es para esa voz. Para entenderla, para no luchar contra ella, y para empezar a moverte de otra manera."}
      </p>

      <Section title="La vergüenza no es debilidad. Es memoria." id="memoria">
        <p>
          {"Lo que sientes no es 'me da pereza'. Es algo más antiguo. La vergüenza del cuerpo no aparece de la nada: es el resultado de años de miradas, comentarios, profesionales que te han juzgado sin saberlo, familiares 'preocupados', ropa que no te queda, fotos que evitas. Cada uno de esos momentos ha dejado un depósito en tu sistema. Ahora, cuando piensas en un gimnasio, ese depósito se activa de golpe."}
        </p>
        <p>
          {"Esto no es psicología barata. Es cómo funciona el cerebro. La vergüenza se guarda en el mismo lugar que el peligro físico — la amígdala. Tu cuerpo reacciona a un gimnasio como reaccionaría a una amenaza real, porque para él ha sido una amenaza real durante años."}
        </p>
        <Pullquote>
          {"No tienes un problema de motivación. Tienes un cuerpo con memoria."}
        </Pullquote>
      </Section>

      <Section title="Por qué el gimnasio no es el primer paso para ti" id="gimnasio">
        <p>
          {"Toda la industria del fitness te venderá que el gimnasio es el lugar donde empieza todo. No es verdad para todo el mundo. Un gimnasio es un espacio diseñado para gente que ya ha superado ciertas barreras: sabe moverse, conoce el vocabulario, conoce el ritual. Si tú no has pasado nunca por esas fases, entrar es como ir a un país nuevo sin hablar el idioma."}
        </p>
        <p>
          {"Podríamos discutir mucho sobre si el problema es el gimnasio o la mirada de los demás. La respuesta es que no importa. Para tu cerebro, el problema es real. Y la solución no es 'obligarte'. La solución es no ir al gimnasio — todavía. Empieza en un lugar que no active la vergüenza, y cuando el cuerpo te lo diga, decidirás si quieres entrar."}
        </p>
      </Section>

      <Section title="Los espacios que sí funcionan cuando empiezas" id="espacios">
        <p>
          {"Hay alternativas al gimnasio tradicional que no activan la vergüenza, y que a menudo funcionan mejor porque están pensadas para personas como tú:"}
        </p>
        <ul className="list-disc pl-6 space-y-2 marker:text-[#B4C5A4]">
          <li>{"En casa. El suelo del salón. Cinco minutos. Nadie te mira. Ningún espejo. Ninguna comparación."}</li>
          <li>{"Paseos cortos por la mañana temprano o al atardecer tarde, cuando hay menos gente en la calle."}</li>
          <li>{"Piscina municipal en horas no punta (11h de la mañana un martes, por ejemplo). El agua tapa y sostiene el cuerpo."}</li>
          <li>{"Clases específicas para cuerpos como el tuyo — cada vez hay más: yoga para personas con obesidad, aquagym adaptada, grupos de caminadores."}</li>
          <li>{"Fisioterapeuta o entrenador personal en una sala privada. Sí, cuesta más dinero que un gimnasio. Pero la única mirada es la de un profesional que está allí para ayudarte, no para juzgarte."}</li>
        </ul>
        <p>
          {"El objetivo del primer año no es 'encontrar mi gimnasio'. Es encontrar el espacio donde tu cuerpo puede empezar a moverse sin miedo. Una vez haya confianza, ya veremos qué hacer."}
        </p>
      </Section>

      <Section title="Qué hacer cuando la vergüenza aparece de golpe" id="aparece">
        <p>
          {"Irás empezando, y un día una voz te dirá: 'qué ridiculez'. O 'no lo estás haciendo bien'. O 'la gente te debe estar mirando'. Es normal. Esa voz no se va de un día para otro. Pero puedes aprender a no obedecerla."}
        </p>
        <p>
          {"Tres cosas que ayudan en el momento:"}
        </p>
        <ul className="list-disc pl-6 space-y-2 marker:text-[#B4C5A4]">
          <li>{"Reconocerla sin discutir. 'Ah, vuelves a estar aquí.' No le dediques más segundos de los necesarios."}</li>
          <li>{"Preguntarte qué haría una persona que no siente lo que sientes tú. Y hacer una versión pequeña de eso mismo. No la versión completa — el 20% que tú puedes hacer hoy."}</li>
          <li>{"Anotar el momento al final del día. No por los sentimientos, sino por el patrón. Con cuatro semanas verás que aparece menos cuando menos caso le haces."}</li>
        </ul>
        <Pullquote>
          {"La vergüenza no hace falta vencerla. Hay que dejarla hablar y seguir caminando."}
        </Pullquote>
      </Section>

      <Section title="La trampa de 'cuando haya perdido un poco'" id="trampa">
        <p>
          {"Mucha gente se dice: 'primero perderé 5 kilos y luego me apunto al gimnasio'. Esta lógica parece razonable. No lo es. Sin moverte, esos 5 kilos no llegarán. Y si llegan (por una dieta agresiva), volverán. La vergüenza no depende del peso — depende de la mirada, y la mirada la llevas tú."}
        </p>
        <p>
          {"Lo que sí pasa cuando empiezas a moverte (aunque no pierdas peso enseguida) es que tu relación con el cuerpo cambia. Empiezas a vivirlo como algo que responde, no como algo que te atrapa. Ese cambio de percepción es lo que hace que un día el gimnasio deje de dar miedo — no los 5 kilos."}
        </p>
      </Section>

      <Section title="La cosa que nadie te dice sobre la vergüenza" id="nadie-dice">
        <p>
          {"A medida que empieces a moverte, la vergüenza no desaparece. Se transforma. El primer mes quizás sientes vergüenza de salir a caminar con mallas. El tercer mes ya no. Pero quizás la sientes por el gesto que haces cuando te levantas de una silla, o por el sonido que haces cuando respiras fuerte. La vergüenza es plástica — se mueve."}
        </p>
        <p>
          {"Lo que cambia no es que desaparezca, es que tu relación con ella cambia. La reconoces antes, la dejas de negar, y empiezas a preguntarte qué te está diciendo en vez de cómo hacerla callar. Algunos días aún ganará. Otros días no. Y eso ya es mucho mejor que ahora."}
        </p>
      </Section>

      <Section title="Un paso concreto que puedes dar hoy" id="paso-hoy">
        <p>
          {"Si has llegado hasta aquí, la vergüenza no te ha ganado aún. Aprovecha eso. Haz una sola cosa hoy: escribe en un papel (o en el móvil) el momento concreto donde apareció la primera mirada que te dio vergüenza. Pueden ser los 12 años, los 20, o el año pasado. No importa cuándo — importa recordar que la vergüenza tiene origen. No es una verdad sobre ti."}
        </p>
        <p>
          {"Este gesto parece pequeño, pero es el primero que separa la vergüenza de tu presente. Una vez la mueves fuera, dentro ya hay un poco más de espacio para moverte sin ella."}
        </p>
        <p>
          {"Si una vez la vergüenza se ha rebajado lo que te queda son intentos empezados y abandonados, vale la pena que leas "}
          <Link to="/es/blog/por-que-abandono-siempre-el-cambio" className="text-[#0B2545] underline decoration-[#B4C5A4] decoration-2 underline-offset-2 hover:decoration-[#0B2545]">
            por qué siempre abandonas — y cómo romper el patrón esta vez
          </Link>
          {". La vergüenza es una barrera, pero el abandono repetido tiene otro origen: un método que te pide voluntad cada día."}
        </p>
        <p>
          {"Si lo que necesitas es un mapa práctico para empezar sin pasar por el gimnasio, "}
          <Link to="/es/blog/como-empezar-a-moverte-con-sobrepeso" className="text-[#0B2545] underline decoration-[#B4C5A4] decoration-2 underline-offset-2 hover:decoration-[#0B2545]">
            esta guía te explica cómo empezar a moverte si tienes sobrepeso
          </Link>
          {" — cinco minutos en la calle, sin mirador y sin prisa."}
        </p>
        <p>
          {"Si quieres entender cómo esta memoria emocional del cuerpo afecta a todas las fases del cambio (no solo la vergüenza en el gimnasio), puedes leer nuestra guía central sobre "}
          <Link to="/es/blog/como-activarte-cuando-llevas-anos-sin-moverte" className="text-[#0B2545] underline decoration-[#B4C5A4] decoration-2 underline-offset-2 hover:decoration-[#0B2545]">
            cómo activarte cuando llevas años sin moverte
          </Link>
          {". Allí explicamos por qué el cuerpo tiene memoria y cómo trabajarla sin forzarte."}
        </p>
        <p>
          {"Y si quieres que te ayudemos a entender cómo afecta la vergüenza a tu punto de partida, déjanos tu correo y te haremos llegar un test inicial con preguntas que te pueden ayudar a verlo más claro."}
        </p>
      </Section>
    </>
  );
}
